import React, { Fragment, useEffect, useState } from 'react';

import { Button, Col, Collapse, Flex, Form, Input, Row, Tag, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { createDesignation, deleteDesignation, getByIdDesignation, getDesignation, getDesignationListsData, setIsSuccess, setIsUpdating, updateDesignation } from './redux/slice';

import DataTable from '../../components/data-table';
import CommonPopconfirm from '../../components/custom-popconfirm';

import ExpandIcon from '../../components/icon/ExpandIcon';
import BackIcon from '../../components/icon/BackIcon';
import EditIcon from '../../components/icon/EditIcon';
import DeleteIcon from '../../components/icon/DeleteIcon';
import CloseIcon from '../../components/icon/CloseIcon';

import { lightPrimary, lightSecondary } from '../../utils/theme/config';

const { Title, Text } = Typography;

const Designation = ({ permission }) => {
    const [form] = Form.useForm();

    const dispatch = useDispatch();
    const navigate = useNavigate()
    const designation = useSelector(state => state.designation);
    const auth = useSelector(state => state.auth);
    const listing = useSelector(state => state.listing);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [designatioLists, setDesignationLists] = useState([]);
    const [modalType, setModalType] = useState(false);
    const [designationEditId, setDesignationEditId] = useState('');
    const { t } = useTranslation();

    useEffect(() => {
        getDesignationList()
    }, [page, pageSize, auth])

    // Set Designations Data 
    useEffect(() => {
        setDesignationLists(designation.designationLists);
        setTotalRecords(designation.totalRecords);
    }, [designation.designationLists, designation.totalRecords]);

    // Get Designation Data
    const getDesignationList = () => {
        dispatch(getDesignationListsData({
            page: page,
            limit: pageSize,
            organizer: auth?.decodeToken?.organizer && auth?.decodeToken?.organizer
        }))
    }

    const handleOnFinish = (values) => {
        if (modalType) {
            dispatch(setIsUpdating(false));
        }
        dispatch(getDesignation({ ...values, organizer: auth?.decodeToken?.organizer }));
    };

    // Create and Update Designation
    useEffect(() => {
        if (Object.keys(designation.designation).length > 0) {
            if (!modalType) {
                dispatch(createDesignation({ apiData: designation.designation, organizerID: auth?.decodeToken?.organizer }));
            } else {
                if (designation.isUpdating) {
                    form.setFieldsValue(designation?.designation);
                } else {
                    dispatch(updateDesignation({ apiData: { ...designation.designation, id: designationEditId }, organizerID: auth?.decodeToken?.organizer }));
                }
            }
        }
        if (designation.isSuccess) {
            handleClear();
            setModalType(false);
            setDesignationEditId('');
            dispatch(setIsSuccess(false));
        }
    }, [designation.isUpdating, designation.designation, designation.isSuccess, designationEditId]);

    // Form reset function
    const handleClear = () => {
        setDesignationEditId('');
        form.resetFields();
        dispatch(getDesignation({}));
    };

    // Expand Icon
    const customExpandIcon = ({ isActive }) => {
        return <ExpandIcon
            style={{
                transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
            }}
            role='button'
        />
    };

    // Pagination function
    const handleOnChange = (page, pageSize) => {
        setPageSize(pageSize);
        setPage(page);
    };

    const handleEditDesignation = (designationId) => {
        setModalType(true);
        setDesignationEditId(designationId);
        dispatch(setIsUpdating(true));
        dispatch(getByIdDesignation(designationId));
    }

    // Table columns
    const columns = [
        {
            title: t('organizer_common_designation'),
            dataIndex: 'name',
            key: 'name',
            render: (text) => <Text>{text}</Text>,
        },
        {
            title: t('organizer_common_status'),
            dataIndex: 'status',
            key: 'status',
            width: '250px',
            render: (_, record) => {
                return (
                    <Fragment>
                        {
                            record?.isActive ?
                                <Tag color={lightPrimary} className='text-center'>{t('organizer_common_active')}</Tag> :
                                <Tag color={lightSecondary} className='text-center'>{t('organizer_common_deactivate')}</Tag>
                        }
                    </Fragment>
                );
            }
        },
    ];

    if (Object.keys(permission)?.length > 0 && (permission?.canUpdate || permission?.canDelete)) {
        columns.push({
            title: <Flex justify='center'><Text>{t('organizer_common_action')}</Text></Flex>,
            key: 'action',
            width: '100px',
            render: (_, record) => {
                return (
                    <Flex justify='center'>
                        {
                            permission?.canUpdate && (
                                <Button
                                    type='text'
                                    className='padding-1'
                                    onClick={() => handleEditDesignation(record?._id)}
                                >
                                    <EditIcon role='button' />
                                </Button>
                            )
                        }
                        {
                            permission?.canDelete && (
                                <Fragment>
                                    {
                                        record?.isActive
                                            ? <CommonPopconfirm
                                                icon={null}
                                                title={t('organizer_usermanagement_designation_deactivateadministrator')}
                                                description={t('organizer_usermanagement_designation_confirmdescription')}
                                                onConfirm={() => dispatch(deleteDesignation({
                                                    id: record?._id,
                                                    page: page,
                                                    limit: pageSize,
                                                    organizer: auth?.decodeToken?.organizer
                                                }))}
                                                cancelButtonProps={{ danger: true, size: 'small', type: 'primary' }}
                                            >
                                                <Button
                                                    type='text'
                                                    className='padding-1'>
                                                    <DeleteIcon role='button' />
                                                </Button>
                                            </CommonPopconfirm>
                                            : <CommonPopconfirm
                                                icon={null}
                                                title={t('organizer_usermanagement_designation_deactivateactiveadministrator')}
                                                description={t('organizer_usermanagement_designation_confirmactivedescription')}
                                                onConfirm={() => dispatch(deleteDesignation({
                                                    id: record?._id,
                                                    page: page,
                                                    limit: pageSize,
                                                    organizer: auth?.decodeToken?.organizer
                                                }))}
                                                cancelButtonProps={{ danger: true, size: 'small', type: 'primary' }}
                                            >
                                                <Button
                                                    type='text'
                                                    className='padding-1'>
                                                    <CloseIcon role='button' />
                                                </Button>
                                            </CommonPopconfirm>
                                    }
                                </Fragment>
                            )
                        }
                    </Flex>
                );
            },
        });
    }

    const items = [
        {
            key: '1',
            label: <Title level={4} type='success' className='text-primary'>{designationEditId ? t('organizer_usermanagement_designation_editdesignation') : t('organizer_usermanagement_addUserDesignation')}</Title>,
            children: <Fragment>
                <Form
                    form={form}
                    className='user-filter'
                    name='designation'
                    colon={false}
                    layout='vertical'
                    onFinish={handleOnFinish}
                    requiredMark={(label, isRule) => {
                        return <Text>{label}<Text type='danger'>{isRule?.required && '*'}</Text></Text>
                    }}
                >
                    <Row gutter={18}>
                        <Col xxl={8} xl={8} lg={8} md={12} sm={24} xs={24} >
                            <Form.Item
                                name='name'
                                label={t('organizer_common_designation')}

                                rules={[{ required: true, message: t('organizer_common_designationerror') }]}
                            >
                                <Input placeholder={t('organizer_common_designation')} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Flex justify='flex-end' gap={10} wrap={true}>
                        <Button danger type='primary' htmlType='button' onClick={handleClear}>{t('organizer_common_cancelButton')}</Button>
                        <Button type='primary' htmlType='submit' loading={designation?.loading}>{t('organizer_common_submitButton')}</Button>
                    </Flex>
                </Form>
            </Fragment>,
        }
    ];

    return (
        <Fragment>
            <Flex vertical gap={12} className='user-management change-tag'>
                <Button
                    type='text'
                    icon={<BackIcon role={'button'} />}
                    className='bg-white paddingX-3'
                    onClick={() => navigate(-1)}
                />
                {
                    (Object.keys(permission)?.length > 0 && permission?.canCreate) &&
                    <Flex className='filter-margin-top'>
                        <Collapse
                            items={items}
                            defaultActiveKey={['1']}
                            className='w-100'
                            bordered={false}
                            expandIcon={customExpandIcon}
                            expandIconPosition={'end'}
                        />
                    </Flex>
                }
                <Flex vertical className='bg-white rounded-top-1'>
                    <DataTable
                        isPagination={true}
                        showSorterTooltip={false}
                        total={totalRecords}
                        pageSize={pageSize}
                        currentPage={page}
                        columns={columns}
                        data={designatioLists}
                        handleOnChange={handleOnChange}
                        rowKey={(record) => record?._id}
                    />
                </Flex>
            </Flex>
        </Fragment>
    );
};

export default Designation;
